/**
 * Public pre-registration types — shapes returned by the anon RPCs in
 * 20261004000002_events_public_preregistration.sql.
 */

import type { PricingRule } from '@/types/pricing.types';

export type RegistrationFormType = 'SIMPLE' | 'FULL_CONVENTION';
export type SignatureMode = 'ON_SITE' | 'DIGITAL';
export type RegistrationKind = 'MINISTER' | 'DELEGATE' | 'GUEST';
export type DocumentKind = 'ci_front' | 'ci_back' | 'voucher' | 'delegation_letter' | 'signature';

export interface NamedOption {
  id: string;
  name: string;
}

export interface PublicEvent {
  id: string;
  code: string;
  name: string;
  description: string | null;
  location: string | null;
  start_date: string;
  end_date: string;
  flyer_filename: string | null;
  base_price: number;
  registration_form_type: RegistrationFormType;
  allow_public_guests: boolean;
  signature_mode: SignatureMode;
  registration_deadline: string | null;
  registration_declaration: string | null;
  payment_instructions: string | null;
  /** File name in EventAssets/PaymentQR/ (public bucket) */
  payment_qr_filename: string | null;
  is_open: boolean;
  /** Partial rows (no audit fields) — enough for calculatePrice() */
  pricing_rules: PricingRule[];
  districts: NamedOption[];
  regions: (NamedOption & { district_id: string })[];
  degrees: NamedOption[];
}

export interface MinisterLookupResult {
  id: string;
  full_name: string | null;
  adb_code: string;
  district_name: string | null;
  region_name: string | null;
  matched_by_ci: boolean;
  already_registered: boolean;
}

export interface MinisterPrefill {
  id: string;
  full_name: string | null;
  dni: string;
  adb_code: string;
  district_id: string | null;
  district_name: string | null;
  region_id: string | null;
  region_name: string | null;
  degree_id: string | null;
  degree_name: string | null;
  position: string | null;
  office_phone: string | null;
  cell_phone: string | null;
  email: string | null;
  address: string | null;
  address_city: string | null;
  address_zone: string | null;
  birth_date: string | null;
  already_registered: boolean;
}

/** Editable form state on the public page */
export interface RegistrationFormValues {
  kind: RegistrationKind;
  minister_id: string | null;
  full_name: string;
  dni: string;
  district_id: string;
  region_id: string;
  degree_id: string;
  position: string;
  office_phone: string;
  cell_phone: string;
  email: string;
  address: string;
  address_city: string;
  address_zone: string;
  birth_date: string;
  represented_minister_id: string | null;
  represented_name: string;
  represented_ci: string;
  institution_note: string;
}

export interface SubmitPreregistrationPayload extends RegistrationFormValues {
  event_id: string;
  documents: Partial<Record<DocumentKind, string>>;
  declaration_accepted: boolean;
  user_agent: string;
  calculated_price: number;
  applied_pricing_rule_id: string | null;
}

export interface RegistrationReceiptRef {
  public_token: string;
  number: number;
  year: number;
  issued_at: string;
}

export interface PublicRegistration {
  event: {
    code: string;
    name: string;
    location: string | null;
    start_date: string;
    end_date: string;
    registration_form_type: RegistrationFormType;
    signature_mode: SignatureMode;
    registration_declaration: string | null;
  };
  registration_status: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  payment_status: string | null;
  attendance_status: string | null;
  calculated_price: number | null;
  registered_at: string;
  declaration_accepted_at: string | null;
  form_signed_at: string | null;
  /** Issued receipts of recorded payments (open each with get_public_receipt) */
  receipts?: RegistrationReceiptRef[];
  kind: RegistrationKind;
  form: Partial<RegistrationFormValues> & {
    adb_code?: string;
    district_name?: string;
    region_name?: string;
    degree_name?: string;
    represented_adb_code?: string;
  };
}

export const DEFAULT_DECLARATION =
  'Declaro que participo conforme al Estatuto Nacional de Las Asambleas de Dios de Bolivia y el presente ' +
  'formulario registra mi participación para efectos legales y respalda las actas que sean suscritas en la ' +
  'Convención Nacional Ordinaria de Las Asambleas de Dios de Bolivia. El presente documento es parte ' +
  'constitutiva de dichas actas.';


// =====================================================
// Edit a registration form (ADB-169) — see 20261029000001_registration_edit_links.sql
// =====================================================

/** Form fields an edit may change; everything else (CI, a minister's master data) is read-only */
export type EditableFormKey =
  | 'full_name'
  | 'cell_phone'
  | 'email'
  | 'office_phone'
  | 'address'
  | 'birth_date'
  | 'position'
  | 'district_id'
  | 'region_id'
  | 'represented_minister_id'
  | 'represented_name'
  | 'represented_ci'
  | 'institution_note';

/** What the edit form shows: the public view of the registration plus lists and which documents exist */
export interface RegistrationEditView extends PublicRegistration {
  event_id: string;
  event_status: string;
  documents: Record<DocumentKind, boolean>;
  districts: NamedOption[];
  regions: (NamedOption & { district_id: string })[];
  /** Only for a link: when it stops working */
  expires_at?: string;
}

export interface RegistrationEditPayload {
  form: Partial<Record<EditableFormKey, string | null>>;
  /** Storage keys of documents uploaded during this edit */
  documents: Partial<Record<DocumentKind, string>>;
}

export interface RegistrationEditResult {
  registration_status: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  signed: boolean;
  documents_changed: boolean;
}

export interface RegistrationEditLink {
  token: string;
  expires_at: string;
  created_at?: string;
  last_used_at?: string | null;
  use_count?: number;
}
